/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Component imports
import { Card, Form, Tabs } from 'antd';
import Permissioned from '../components/Permissioned';
import DocumentTitle from '../components/DocumentTitle';
import LiveChatGeneralSettingsForm from '../components/Forms/LiveChatGeneralSettingsForm';
import LiveChatCannedResponsesSettingsTable from '../components/Tables/LiveChatCannedResponsesSettingsTable';
import LiveChatAutomationsSettingsTable from '../components/Tables/LiveChatAutomationsSettingsTable';
import LiveChatBotSettingsForm from '../components/Forms/LiveChatBotSettingsForm';
import DownloadLiveChatConversationsPanel from '../components/UI/DownloadLiveChatConversationsPanel';

// Misc imports
import { ADDITIONAL_ROLES_ORG, PERMISSIONS_ORG } from '../constants/main';
import { useTranslation } from 'react-i18next';
import { LIVECHAT_SCREEN_TABS } from '../constants/liveChat';
import useUserPermissions from '../utils/hooks/useUserPermissions';

// Constant declarations
const ALLOWED_PERMISSIONS = [PERMISSIONS_ORG.ADMIN, ADDITIONAL_ROLES_ORG.LIVECHAT_ADMIN];
const TABS = {
  GENERAL: 'general',
  CANNED_RESPONSES: 'canned-responses',
  AUTOMATIONS: 'automations',
  BOT_SETTINGS: 'bot-settings',
  TRANSCRIPTIS: 'transcripts'
};

const LiveChatSettingsScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const { orgId } = useParams();
  const { t } = useTranslation();
  const userPermissions = useUserPermissions(orgId, null);
  const [form] = Form.useForm();

  let currentTab = searchParams.get('settings-tab') || TABS.GENERAL;

  const onTabChange = activeKey => {
    navigate({
      pathname: location.pathname,
      search: `?tab=${LIVECHAT_SCREEN_TABS.ADMIN_SETTINGS}&settings-tab=${activeKey}`
    }, { replace: true });
  };

  return (
    <Fragment>
      <DocumentTitle title={t('Admin Panel')} />

      <Permissioned
        allowedPermissions={ALLOWED_PERMISSIONS}
        currentPermissions={userPermissions}
      >
        <Tabs
          activeKey={currentTab}
          destroyInactiveTabPane
          onTabClick={(key) => {
            if (key !== searchParams.get('tab')) {
              return onTabChange(key);
            }
          }}
          items={[
            {
              key: TABS.GENERAL,
              label: t('Settings'),
              children: <LiveChatGeneralSettingsForm />
            },
            {
              key: TABS.CANNED_RESPONSES,
              label: t('Canned Responses'),
              children: <LiveChatCannedResponsesSettingsTable/>
            },
            {
              key: TABS.AUTOMATIONS,
              label: t('Automations'),
              children: <LiveChatAutomationsSettingsTable/>
            },
            {
              key: TABS.BOT_SETTINGS,
              label: t('Agent Settings'),
              children: (
                <Fragment>
                  <Card className="mb-4">
                    <LiveChatBotSettingsForm form={form}/>
                  </Card>
                </Fragment>
              )
            },
            {
              key: TABS.TRANSCRIPTIS,
              label: t('Transcripts'),
              children: <DownloadLiveChatConversationsPanel />
            }
          ]}
        />
      </Permissioned>
    </Fragment>
  );
};

export default LiveChatSettingsScreen;
